import * as React from 'react';
import { InputAppearance } from '../helpers/input';
import { Text, TextTag, TokenTextAppearance, TokenTextColor } from '../text';
import { styled, ThemeProvider } from '../theme-provider';
import { useThumbKeyHandlers, usePointerDownHandler } from './thumb-interaction';
import { createA11yOutlineStyles } from '../helpers/a11y';
export const THUMB_SIZE = 18; //px
// Extra space the bubble needs to its sides before "docking" on
const BUBBLE_MARGIN = 4; //px
const StyledThumbWrapper = styled.div.attrs(({ theme, xFraction, isSliding }) => {
    const isRtl = theme.direction === 'rtl';
    const leftXFraction = isRtl ? 1 - xFraction : xFraction;
    return {
        style: {
            willChange: isSliding ? 'transform' : undefined,
            '--leftPercent': `${leftXFraction * 100}%`,
        },
    };
}).withConfig({ displayName: "StyledThumbWrapper", componentId: "sc-1vy4hf2" }) `width:100%;height:100%;position:absolute;top:0;left:0;pointer-events:none;transform:translate(var(--leftPercent), 50%);touch-action:none;& > *{pointer-events:${(props) => (props.isBuried ? 'none' : 'auto')};cursor:${(props) => (props.isInactive ? 'not-allowed' : 'grab')};}${(props) => !props.isInactive &&
    `
		:hover {
			${StyledThumb}::before {
				opacity: 1;
			}
		}
		&:active > * {
			cursor: grabbing;
		}
	`} z-index:${(props) => props.zIndex};&:focus-within{z-index:9999;}--primary-color:${({ isInactive, theme }) => isInactive
    ? theme.colors.content.secondary
    : theme.colors.controllers.on.default};`;
const StyledThumb = styled.div.withConfig({ displayName: "StyledThumb", componentId: "sc-wd4b0h" }) `border:${(props) => props.theme.size.border.heavy} solid var(--primary-color);background-color:var(--primary-color);position:absolute;left:0;top:0;transform:translate(-50%, -50%);transition:transform 0.2s;${(props) => props.isBuried &&
    `
		:not(:focus) {
			transform: translate(-50%, 0) scale(0.8);
		}
	`} user-select:none;display:flex;box-sizing:border-box;height:${THUMB_SIZE}px;width:${THUMB_SIZE}px;border-radius:100%;::before{content:'';border-radius:100px;position:absolute;top:0;right:0;left:0;bottom:0;background-color:${(props) => `${props.theme.colors.content.inverted}33` // for the 0.2 opacity
};opacity:0;}*:active > &,&:focus{outline:none;border:unset;background-color:${(props) => props.theme.colors.controllers.on.default};}&:focus-visible{${({ theme }) => createA11yOutlineStyles(theme, 'solid')}}`;
// estimate of total height needed for value bubbles, so we leave enough space
// to the top in the slider component.
//
// space below and above bubble + padding in bubble + text
export const getValueBubbleHeight = (theme) => `calc(2 * ${theme.size.static200} + 2 * ${theme.size.static100} + 1rem)`;
const createDropShadows = (theme) => {
    const shadows = theme.colors.shadow.variant1.split(/,(?![^(#]*[,)])/g);
    return shadows.map((shadow) => `drop-shadow(${shadow})`).join(' ');
};
const StyledValueBubbleWrapper = styled.div.withConfig({ displayName: "StyledValueBubbleWrapper", componentId: "sc-16f79q2" }) `position:absolute;bottom:0;left:0;transform:translateX(-50%)translateY(\n\t\t\tcalc(-${THUMB_SIZE / 2}px - ${(props) => props.theme.size.static200})\n\t\t);color:${({ isInactive, theme }) => isInactive
    ? theme.colors.content.secondary
    : theme.colors.controllers.on.default};--bubble-background:${({ theme, isDisabled }) => isDisabled ? 'transparent' : theme.colors.background.primary};&::before{position:absolute;display:block;content:'';background-color:var(--bubble-background);height:${(props) => props.theme.size.static300};width:${(props) => props.theme.size.static300};border-radius:100px 100px 0 100px;bottom:0;left:50%;transform:translate(-50%, 5%)rotate(45deg);}visibility:${(props) => (props.isDisabled ? 'hidden' : 'initial')};filter:${(props) => createDropShadows(props.theme)};`;
const StyledValueBubble = styled.div.attrs((props) => {
    const { theme, railWidth, xFraction } = props;
    const isRtl = theme.direction === 'rtl';
    // the rail has padding of one thumb to its sides
    const railEdgeOffset = THUMB_SIZE - BUBBLE_MARGIN;
    return {
        style: {
            '--railWidthLeft': `${(isRtl ? railWidth * (1 - xFraction) : railWidth * xFraction) +
                railEdgeOffset}px`,
            '--railWidthRight': `${(isRtl ? railWidth * xFraction : railWidth * (1 - xFraction)) +
                railEdgeOffset}px`,
        },
    };
}).withConfig({ displayName: "StyledValueBubble", componentId: "sc-1kwvl6a" }) `margin:0 10px;border-radius:100px;padding:${(props) => `${props.theme.size.static100} ${props.theme.size.static200}`};background-color:var(--bubble-background);min-width:${(props) => props.theme.size.static500};text-align:center;transform:translateX(\n\t\t\tclamp(-1 * var(--railWidthLeft), -50%, -100% + var(--railWidthRight))\n\t\t)translateX(50%);user-select:none;`;
export const Thumb = ({ value, valueText, ariaValueText, isSliding, appearance, isBuried, railElementRef, zIndex, sliderMin, sliderMax, onValueChange, onSlideChange, onFocus, thumbMin, thumbMax, ariaLabel, ariaLabelledBy, ariaDescribedBy, keyboardSteps, }) => {
    const thumbRef = React.useRef(null);
    const isDisabled = appearance === InputAppearance.Disabled;
    const isReadOnly = appearance === InputAppearance.ReadOnly;
    // set a plausible default for rough estimate calculation in SSR
    const [railWidth, setRailWidth] = React.useState(400);
    React.useEffect(() => {
        if (!railElementRef.current) {
            return;
        }
        setRailWidth(railElementRef.current.getBoundingClientRect().width);
        const io = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we are observing railElementRef.current, so it cannot be null here
                setRailWidth(railElementRef.current.getBoundingClientRect().width);
            }
        });
        io.observe(railElementRef.current);
        return () => {
            io.disconnect();
        };
    }, [railElementRef]);
    const pointerDownHandler = usePointerDownHandler(sliderMin, sliderMax, railElementRef, thumbRef, onSlideChange, onValueChange, isDisabled || isReadOnly);
    const keyHandlers = useThumbKeyHandlers(sliderMin, sliderMax, keyboardSteps, onSlideChange, onValueChange, isDisabled || isReadOnly);
    /** calculate "percentage" as fraction of 1 */
    const xFraction = (value - sliderMin) / (sliderMax - sliderMin);
    return (React.createElement(StyledThumbWrapper, { isSliding: isSliding, isBuried: isBuried, xFraction: xFraction, zIndex: zIndex, isInactive: isDisabled || isReadOnly, isDisabled: isDisabled, onPointerDown: pointerDownHandler },
        React.createElement(StyledThumb, Object.assign({ tabIndex: isDisabled ? undefined : 0, ref: thumbRef, role: "slider", "aria-valuemax": thumbMax, "aria-valuemin": thumbMin, "aria-valuenow": value, "aria-valuetext": ariaValueText || valueText, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-disabled": isDisabled, "aria-readonly": isReadOnly, isBuried: isBuried, onFocus: onFocus }, keyHandlers)),
        valueText && (React.createElement(ThemeProvider, { theme: "main" },
            React.createElement(StyledValueBubbleWrapper, { isInactive: isDisabled || isReadOnly, isDisabled: isDisabled },
                React.createElement(StyledValueBubble, { xFraction: xFraction, railWidth: railWidth },
                    React.createElement(Text, { bold: true, tag: TextTag.span, color: TokenTextColor.inherit, appearance: TokenTextAppearance.label150 }, valueText)))))));
};
